import { inject, Injectable } from '@angular/core';
import { Client } from '@app/core/model/client/client';
import { User, UserInput } from '@app/core/model/client/user';
import ApiService from '@services/api.service';
import { gql } from 'apollo-angular';
import { plainToInstance } from 'class-transformer';
import { map, Observable, tap } from 'rxjs';

@Injectable({providedIn: 'root'})
export class UsersService {
  private readonly apiService = inject(ApiService);

  private userFragment = gql`
    fragment UserData on User {
      id
      name
      email
      isTb
      lastLogin
      lastInvite
      userRole {
        id
        code
      }
      displayableModules {
        id
        module {
          id
          code
          order
          isDefault
        }
      }
      groups {
        id
        name
        client {
          id
          organizationId
        }
      }
      clients {
        id
        name
        organizationId
        subscriptions {
          id
          nbUser
          endDate
          startDate
        }
      }
      picture
      workspaceId
    }
  `;

  /**
   * Send an API request for fetching users.
   * @param tb Filter for retrieving all users, only tb users, or only non-tb users.
   * @param sameClient Whether to return only users belonging to the same client as the authenticated user's.
   * @return Observable emitting a list of Users.
   */
  public getUsers(tb: 'ALL' | 'ONLYTB' | 'NOTTB' = 'ALL', sameClient: boolean = false): Observable<User[]> {
    const query = gql`
      query Users($tb: TbEnum, $sameClient: Boolean) {
        users(tb: $tb, sameClients: $sameClient) {
          ...UserData
        }
      }
      ${this.userFragment}
    `;
    const variables = {tb, sameClient};
    return this.apiService.query({query, variables})
      .pipe(map(data => plainToInstance(User, data['users'] as User[])));
  }

  /**
   * Check whether an email address is currently being used by a user.
   * @param email Email address to check.
   * @return Observable that emits a single boolean value indicating whether a user was found.
   */
  public userExists(email: string): Observable<boolean> {
    const query = gql`
      query UserExists($email: String!) {
        userExists(email: $email)
      }
    `;
    const variables = {email};
    return this.apiService.query({query, variables})
      .pipe(map(data => data['userExists'] as boolean));
  }

  /**
   * Send an API request for creating a new user. If a client is provided, the new user will be added to its list of
   * user members.
   * @param email New user's email address.
   * @param userInput User's input data.
   * @param client Optional client to which the user belongs.
   * @return Observable emitting the new User.
   */
  public createUser(email: string, userInput: UserInput, client?: Client): Observable<User> {
    const mutation = gql`
      mutation CreateUser($email: String!, $clientId: Int, $user: UserInput!) {
        createUser(email: $email, clientId: $clientId, user: $user) {
          ...UserData
        }
      }
      ${this.userFragment}
    `;
    const variables = {
      email,
      clientId: client?.id,
      user: userInput,
    };
    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => plainToInstance(User, data['createUser'])),
        tap(user => {
          if (client) {
            client.users.push(user);
          }
        })
      );
  }

  /**
   * Send an API request for updating a user.
   * @param user User to update.
   * @param userInput Update data.
   * @param client Optional client to which the user belongs.
   */
  public updateUser(user: User, userInput: UserInput, client?: Client): Observable<User> {
    const mutation = gql`
      mutation UpdateUser($user: UserInput!, $userId: Int!, $clientId: Int) {
        updateUser(id: $userId, user: $user, clientId: $clientId) {
          ...UserData
        }
      }
      ${this.userFragment}
    `;
    const variables = {
      userId: user.id,
      clientId: client?.id,
      user: userInput
    };
    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => plainToInstance(User, data['updateUser'])),
        tap(updatedUser => {
          if (client) {
            client.updateUser(updatedUser);
          }
        })
      );
  }

  /**
   * Send an API request for deleting a list of users.
   * @param users Users to delete.
   * @return Observable emitting a boolean indicating whether the users were deleted successfully.
   */
  public deleteUsers(users: User[]): Observable<boolean> {
    const mutation = gql`
      mutation DeleteUser($userIds: [Int!]!) {
        deleteUsers(ids: $userIds)
      }
    `;
    const variables = {
      userIds: users.map(user => user.id)
    };
    return this.apiService.mutate({
      mutation,
      variables,
      update: cache => {
        // Remove deleted users from apollo cache
        users.forEach(user => cache.evict({
          id: cache.identify({id: user.id, __typename: 'User'})
        }));
      }
    })
      .pipe(map(data => data['deleteUsers'] as boolean));
  }

  /**
   * Send or resend an invitation to a User. A related Client must be provided to generate an invitation which will
   * allow the user to sign up using the authentication method of that client.
   * @param user User to whom to send an invitation.
   * @param client Client through which to invite the user to the app.
   * @return An Observable that will emit the User once their invitation has been sent, with lastInvite updated.
   */
  public sendInvitation(user: User, client: Client): Observable<User> {
    const mutation = gql`
      mutation SendInvitation($userId: Int!, $clientId: Int!) {
        inviteUser(userId: $userId, clientId: $clientId) {
          id
          name
          lastInvite
        }
      }
    `;
    const variables = {
      userId: user.id,
      clientId: client.id
    };
    return this.apiService.mutate({mutation, variables})
      .pipe(map(data => plainToInstance(User, data['inviteUser'])));
  }
}
